import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  FaTimes,
  FaEraser,
  FaChevronUp,
  FaChevronDown,
  FaSpinner,
  FaCheck,
  FaBan,
  FaUndo,
  FaRedo,
  FaLayerGroup
} from 'react-icons/fa';
import { Button, IconButton, Dropdown, DropdownItem } from '../common';
import type { EditModeContextState, DecorationItem, EditMode } from '../../contexts/editMode.types.js';

interface DecorationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  editMode: EditModeContextState;
}

export function DecorationDrawer({
  isOpen,
  onClose,
  editMode
}: DecorationDrawerProps) {
  const drawerRef = useRef<HTMLDivElement>(null);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  // Handle close with unsaved changes check
  const handleCloseClick = useCallback(() => {
    if (editMode.hasUnsavedChanges) {
      setShowConfirmCancel(true);
    } else {
      editMode.exitEditMode();
      onClose();
    }
  }, [editMode.hasUnsavedChanges, editMode.exitEditMode, onClose]);

  // Handle escape key to close drawer
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleCloseClick();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, handleCloseClick]);

  // Handle click outside drawer
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target as Node)) {
        // Check if click is on the overlay
        const target = e.target as HTMLElement;
        if (target.classList.contains('decoration-overlay')) {
          handleCloseClick();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, handleCloseClick]);

  // Handle undo/redo keyboard shortcuts (Ctrl+Z / Ctrl+Y / Ctrl+Shift+Z)
  useEffect(() => {
    const handleUndoRedo = (e: KeyboardEvent) => {
      if (!isOpen || !editMode.isActive) return;

      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        editMode.undo();
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        editMode.redo();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleUndoRedo);
    }

    return () => {
      document.removeEventListener('keydown', handleUndoRedo);
    };
  }, [isOpen, editMode.isActive, editMode.undo, editMode.redo]);

  // Handle cancel confirmation
  const handleConfirmCancel = () => {
    editMode.rollbackChanges();
    editMode.exitEditMode();
    setShowConfirmCancel(false);
    onClose();
  };

  // Handle keep editing
  const handleKeepEditing = () => {
    setShowConfirmCancel(false);
  };

  // Handle mode change
  const handleModeChange = (mode: EditMode) => {
    editMode.setMode(mode);
    // Don't call selectDecoration when switching to erase mode
    // as it would override the mode back to 'select'
  };

  // Handle decoration selection (disabled in erase mode)
  const handleDecorationSelect = (decoration: DecorationItem) => {
    if (editMode.mode === 'erase') return;
    if (editMode.selectedDecoration?.id === decoration.id) {
      // Deselect if already selected
      editMode.selectDecoration(null);
      editMode.setMode('select');
    } else {
      // Select the decoration and automatically switch to place mode
      editMode.selectDecoration(decoration);
      // Mode will be automatically set to 'place' by selectDecoration
    }
  };

  // Handle category change
  const handleCategoryChange = (category: string) => {
    editMode.setSelectedCategory(category);
  };

  // Handle accept changes
  const handleAccept = async () => {
    const success = await editMode.commitChanges();
    if (success) {
      editMode.exitEditMode();
      onClose();
    }
  };

  // Handle cancel changes
  const handleCancel = () => {
    if (editMode.hasUnsavedChanges) {
      setShowConfirmCancel(true);
    } else {
      editMode.exitEditMode();
      onClose();
    }
  };

  // Get decoration item styles
  const getDecorationItemClasses = (decoration: DecorationItem) => {
    const isSelected = editMode.selectedDecoration?.id === decoration.id;
    const isErase = editMode.mode === 'erase';

    let classes = 'relative aspect-square border-2 rounded-lg p-2 cursor-pointer transition-all duration-200 ';

    if (isErase) {
      classes += 'border-transparent opacity-40 cursor-default ';
    } else if (isSelected) {
      classes += 'border-game-golden bg-game-golden/20 shadow-md shadow-game-golden/30 ';
    } else {
      classes += 'border-transparent hover:border-white hover:bg-game-medium/30 ';
    }

    return classes;
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="decoration-overlay fixed inset-0 bg-black/30 z-[999] md:hidden"
          onClick={handleCloseClick}
        />
      )}

      {/* Drawer */}
      <div
        ref={drawerRef}
        className={`decoration-drawer fixed right-0 top-0 bottom-16 w-full md:w-[400px] bg-game-dark border-l border-gray-700
                    transform transition-transform duration-200 ease-in-out z-[2000]
                    ${isOpen ? 'translate-x-0' : 'translate-x-full'}
                    shadow-2xl overflow-hidden flex flex-col`}
      >
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-gray-700 bg-game-dark/50">
          <div className="flex items-center gap-2">
            <FaLayerGroup className="text-game-golden text-lg" />
            <h2 className="text-white font-semibold text-lg">Customize Island</h2>
          </div>
          <IconButton
            icon={<FaTimes />}
            onClick={handleCloseClick}
            ariaLabel="Close decoration editor"
            size="sm"
          />
        </div>

        {/* Toolbar */}
        <div className="flex-shrink-0 px-4 py-3 border-b border-gray-700 bg-game-medium/20">
          <div className="flex items-center justify-between gap-3">
            {/* Mode Controls */}
            <div className="flex items-center gap-2">
              <IconButton
                icon={<FaEraser />}
                onClick={() => handleModeChange(editMode.mode === 'erase' ? 'select' : 'erase')}
                ariaLabel="Eraser tool"
                size="sm"
                variant={editMode.mode === 'erase' ? 'active' : 'default'}
                className={editMode.mode === 'erase' ? 'bg-game-orange text-white' : ''}
              />
              <IconButton
                icon={<FaUndo />}
                onClick={() => editMode.undo()}
                ariaLabel="Undo"
                size="sm"
                disabled={!editMode.canUndo}
              />
              <IconButton
                icon={<FaRedo />}
                onClick={() => editMode.redo()}
                ariaLabel="Redo"
                size="sm"
                disabled={!editMode.canRedo}
              />
            </div>

            {/* Depth Controls - adjust depth of selected placed decoration */}
            <div className="flex items-center gap-1">
              <span className="text-gray-400 text-xs">Depth:</span>
              <IconButton
                icon={<FaChevronUp />}
                onClick={() => {
                  if (editMode.selectedPlacedDecorationId) {
                    editMode.adjustDecorationDepth(editMode.selectedPlacedDecorationId, 'up');
                  }
                }}
                ariaLabel="Move to front"
                size="sm"
                disabled={!editMode.selectedPlacedDecorationId}
              />
              <IconButton
                icon={<FaChevronDown />}
                onClick={() => {
                  if (editMode.selectedPlacedDecorationId) {
                    editMode.adjustDecorationDepth(editMode.selectedPlacedDecorationId, 'down');
                  }
                }}
                ariaLabel="Move to back"
                size="sm"
                disabled={!editMode.selectedPlacedDecorationId}
              />
            </div>

            {/* Staged Changes Info */}
            {editMode.hasUnsavedChanges && (
              <div className="flex items-center gap-2 text-game-golden text-xs">
                <div className="w-2 h-2 bg-game-golden rounded-full animate-pulse" />
                {editMode.stagedChanges.length} change{editMode.stagedChanges.length !== 1 ? 's' : ''}
              </div>
            )}
          </div>
        </div>

        {/* Category Selector */}
        <div className="flex-shrink-0 px-4 py-3 border-b border-gray-700">
          <Dropdown
            trigger={({ isOpen, toggle }) => (
              <Button
                variant="secondary"
                onClick={toggle}
                className="w-full px-3 py-2 text-sm justify-between"
              >
                <span className="font-medium truncate">
                  {editMode.selectedCategory === 'all' ? 'All Categories' : editMode.selectedCategory}
                </span>
                <FaChevronDown className={`text-gray-400 text-xs transition-transform duration-200 flex-shrink-0 ml-2 ${isOpen ? 'rotate-180' : ''}`} />
              </Button>
            )}
            position="below"
            className="max-h-60 overflow-y-auto"
          >
            {editMode.categories.map(category => (
              <DropdownItem
                key={category}
                label={category === 'all' ? 'All Categories' : category}
                onClick={() => handleCategoryChange(category)}
                className={editMode.selectedCategory === category ? 'bg-game-medium/40' : ''}
              />
            ))}
          </Dropdown>
        </div>

        {/* Decoration Palette */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="p-4">
            <div className="grid grid-cols-3 gap-3">
              {editMode.availableDecorations.map((decoration) => (
                <div
                  key={decoration.id}
                  className={getDecorationItemClasses(decoration)}
                  onClick={() => handleDecorationSelect(decoration)}
                  title={decoration.name}
                >
                  {/* Decoration preview */}
                  {decoration.previewUrl ? (
                    <img
                      src={decoration.previewUrl}
                      alt={decoration.name}
                      className="w-full h-full object-contain rounded-md"
                      style={{ imageRendering: 'pixelated' }}
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-game-medium to-game-light/50 rounded-md flex items-center justify-center">
                      <span className="text-xs text-gray-300 font-medium text-center px-1">
                        {decoration.name}
                      </span>
                    </div>
                  )}

                  {/* Selection indicator */}
                  {editMode.selectedDecoration?.id === decoration.id && editMode.mode !== 'erase' && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-game-golden rounded-full flex items-center justify-center">
                      <FaCheck className="text-game-dark text-xs" />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {editMode.availableDecorations.length === 0 && (
              <div className="text-center text-gray-400 py-8">
                <FaLayerGroup className="mx-auto text-3xl mb-3 opacity-50" />
                <p>No decorations in this category</p>
              </div>
            )}
          </div>
        </div>

        {/* Error Display */}
        {editMode.error && (
          <div className="flex-shrink-0 mx-4 mb-3 p-3 bg-game-orange/20 border border-game-orange/50 rounded-lg">
            <p className="text-game-orange text-sm">{editMode.error}</p>
          </div>
        )}

        {/* Footer Actions */}
        <div className="flex-shrink-0 flex items-center justify-between gap-3 px-4 py-3 border-t border-gray-700 bg-game-dark/50">
          <Button
            variant="secondary"
            onClick={handleCancel}
            className="flex-1"
            disabled={editMode.isLoading}
          >
            <FaBan className="text-sm" />
            Cancel
          </Button>

          <Button
            variant="primary"
            onClick={handleAccept}
            className="flex-1"
            disabled={editMode.isLoading || !editMode.hasUnsavedChanges}
            isLoading={editMode.isLoading}
          >
            {editMode.isLoading ? (
              <FaSpinner className="text-sm animate-spin" />
            ) : (
              <FaCheck className="text-sm" />
            )}
            Accept Changes
          </Button>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmCancel && (
        <div className="fixed inset-0 bg-black/50 z-[2001] flex items-center justify-center p-4">
          <div className="bg-game-dark border border-gray-700 rounded-lg p-6 max-w-md w-full">
            <h3 className="text-white font-semibold text-lg mb-3">Unsaved Changes</h3>
            <p className="text-gray-300 mb-6">
              You have unsaved changes. Are you sure you want to exit without saving?
            </p>
            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={handleKeepEditing}
                className="flex-1"
              >
                Keep Editing
              </Button>
              <Button
                variant="danger"
                onClick={handleConfirmCancel}
                className="flex-1"
              >
                <FaUndo className="text-sm" />
                Discard Changes
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};